import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { LandingPage } from './pages/LandingPage';
import { AuthPage } from './pages/AuthPage';
import { ProfessionalAuthPage } from './pages/ProfessionalAuthPage';
import { StudentAuthPage } from './pages/StudentAuthPage';
import { PaymentPage } from './pages/PaymentPage';
import { InstructionsPage } from './pages/InstructionsPage';
import { AssessmentPage } from './pages/AssessmentPage';
import { DashboardPage } from './pages/DashboardPage';
import { AdminDashboard } from './pages/AdminDashboard';
import { ThankYouPage } from './pages/ThankYouPage';
import { CompatibilityReportPage } from './pages/CompatibilityReportPage';
import { ReportQAPage } from './pages/ReportQAPage';
import TranslationTestPage from './pages/TranslationTestPage';
// Enterprise Pages
import GatewayPage from './pages/GatewayPage';
import CorporateRegistrationPage from './pages/CorporateRegistrationPage';
import CorporateLoginPage from './pages/CorporateLoginPage';
import CorporateDashboard from './pages/CorporateDashboard';
import InternationalStudentAuthPage from './pages/InternationalStudentAuthPage';
import { Toaster } from './components/ui/sonner';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 border-4 border-[#CC5500]/20 rounded-full" />
          <div className="absolute inset-0 border-4 border-transparent border-t-[#CC5500] rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return user ? children : <Navigate to="/auth" />;
};

const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 border-4 border-[#CC5500]/20 rounded-full" />
          <div className="absolute inset-0 border-4 border-transparent border-t-[#CC5500] rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" />;
  }

  if (!isAdmin) {
    return <Navigate to="/" />;
  }

  return children;
};

function AppContent() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/login/professional" element={<ProfessionalAuthPage />} />
          <Route path="/login/student" element={<StudentAuthPage />} />
          <Route path="/professional-auth" element={<ProfessionalAuthPage />} />
          <Route path="/student-auth" element={<StudentAuthPage />} />
          <Route
            path="/payment"
            element={
              <ProtectedRoute>
                <PaymentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructions"
            element={
              <ProtectedRoute>
                <InstructionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessment"
            element={
              <ProtectedRoute>
                <AssessmentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/thank-you"
            element={
              <ProtectedRoute>
                <ThankYouPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/report-qa"
            element={
              <ProtectedRoute>
                <ReportQAPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/compatibility"
            element={
              <AdminRoute>
                <CompatibilityReportPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/translation-test"
            element={
              <AdminRoute>
                <TranslationTestPage />
              </AdminRoute>
            }
          />
          
          {/* Enterprise / Corporate Routes */}
          <Route path="/gateway" element={<GatewayPage />} />
          <Route path="/corporate/register" element={<CorporateRegistrationPage />} />
          <Route path="/corporate/login" element={<CorporateLoginPage />} />
          <Route path="/corporate/dashboard" element={<CorporateDashboard />} />
          
          {/* International Students - FREE Access */}
          <Route path="/international" element={<InternationalStudentAuthPage />} />
          <Route path="/login/international" element={<InternationalStudentAuthPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;